// Copyright 2017 Sorama B.V.

import {
    bindable,
    computedFrom,
    customElement,
    BindingEngine,
    autoinject,
    Disposable,
    bindingMode,
    observable,
} from "aurelia-framework";
import { Pair, Rect, Size, Margin } from "controls/plot/IPlotPart";
import { PlotParentBase } from "controls/plot/PlotParentBase";
import { Corners } from "controls/plot/scan-planner/ScanPlanExtensions";
import { ScanArea } from "datatypes/ScanStep";
import { OrderedRecordingStepGroup } from "libraries/Scanning/OrderedRecordingSteps";
import { Length } from "units/Length";
import { Unit } from "units/Unit";

@customElement("scan-planner")
@autoinject
export class ScanPlanner extends PlotParentBase {
    //#region Constants
    public static planners = 0;
    private scaleFactor = 1;
    //#endregion

    //#region Injected Services
    private bindingEngine: BindingEngine;
    //#endregion

    //#region Bindables

    // Input Bindables

    // Required
    @bindable
    public mode: "plan" | "scan" = "plan";

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public steps: OrderedRecordingStepGroup[];

    @bindable
    public disabled: boolean;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public currentStep: OrderedRecordingStepGroup;

    //#endregion

    //#region Computed Properties

    @observable
    public startCorner: Corners = Corners.TopLeft;

    @observable
    public strideDirection: "x" | "y" = "x";

    @computedFrom("steps")
    public get scanArea(): ScanArea {
        if (!this.steps) {
            return <ScanArea>{
                width: 0,
                height: 0,
                offsetX: 0,
                offsetY: 0,
            };
        }

        const offsetX = Math.min(...this.steps.map((x) => x.scanArea.offsetX));
        const offsetY = Math.min(...this.steps.map((x) => x.scanArea.offsetY));

        const maxPosX = Math.max(...this.steps.map((x) => x.scanArea.width + x.scanArea.offsetX));
        const maxPosY = Math.max(...this.steps.map((x) => x.scanArea.height + x.scanArea.offsetY));

        return <ScanArea>{
            offsetX: offsetX,
            offsetY: offsetY,
            width: maxPosX - offsetX,
            height: maxPosY - offsetY,
        };
    }

    @computedFrom("scanArea")
    public get svgWidth(): number {
        if (!this.steps) return 1;

        return this.scanArea.width * this.scaleFactor;
    }

    @computedFrom("scanArea")
    public get svgHeight(): number {
        if (!this.steps) return 1;

        return this.scanArea.height * this.scaleFactor;
    }

    @computedFrom("svgWidth", "svgHeight")
    public get viewBox(): string {
        return `0 ${-1 * this.svgHeight} ${this.svgWidth} ${this.svgHeight}`;
    }

    @computedFrom("mode", "steps")
    public get canSelectStrideDirection(): boolean {
        const horizontalChange =
            Math.max(...this.steps.map((x) => x.positionInArrays.x)) -
                Math.min(...this.steps.map((x) => x.positionInArrays.x)) !=
            0;
        const verticalChange =
            Math.max(...this.steps.map((x) => x.positionInArrays.y)) -
                Math.min(...this.steps.map((x) => x.positionInArrays.y)) !=
            0;
        return this.mode == "plan" && horizontalChange && verticalChange;
    }

    //#endregion

    //#region Fields
    private actualSize: Size;
    private subscriptions: Disposable[];

    // Required for clipping path
    private plannerId: number;
    //#endregion

    //#region Constructor and Initialization

    constructor(bindingEngine: BindingEngine) {
        super();
        this.bindingEngine = bindingEngine;
        this.plannerId = ScanPlanner.planners++;

        this.subscriptions = [];
    }

    public bind(): void {
        this.subscriptions = [
            this.bindingEngine
                .propertyObserver(this.controlContainer, "clientWidth")
                .subscribe(() => this.requestMeasure()),
            this.bindingEngine
                .propertyObserver(this.controlContainer, "clientHeight")
                .subscribe(() => this.requestMeasure()),
        ];

        this.updateSteps();
    }

    public attached(): void {
        this.requestMeasure();
        this.requestViewPort();
    }

    public unbind(): void {
        this.subscriptions.forEach((x) => x.dispose());
        this.subscriptions = [];
    }

    //#endregion

    //#region IPlotParent
    public requestMeasure(): void {
        if (!this.controlContainer || !this.steps) return;

        // Measure children
        const margin = new Margin(0, 0, 0, 0);

        this.plotParts.forEach((x) => {
            const partMargin = x.onMeasureMargin();

            margin.left = Math.max(margin.left, partMargin.left);
            margin.top = Math.max(margin.top, partMargin.top);
            margin.right = Math.max(margin.right, partMargin.right);
            margin.bottom = Math.max(margin.bottom, partMargin.bottom);
        });

        this.margin = margin;

        // Determine size based on the aspect ratio
        const aspectRatio = this.scanArea.width / this.scanArea.height;

        const availableWidth = this.controlContainer.clientWidth;
        const availableHeight = this.controlContainer.clientHeight;

        if (availableWidth <= 0 || availableHeight <= 0) return;

        const marginx = this.margin.left + this.margin.right;
        const marginy = this.margin.top + this.margin.bottom;

        const svgMaxWidth = Math.max(availableWidth - marginx, marginx);
        const svgMaxHeight = Math.max(availableHeight - marginy, marginy);

        const wideSize = new Size(svgMaxWidth + marginx, svgMaxWidth / aspectRatio + marginy);
        const tallSize = new Size(svgMaxHeight * aspectRatio + marginx, svgMaxHeight + marginy);

        // Pick the size based on the aspect ratio (size1 for wide plans, size2 for tall plans) and available width and height
        if (aspectRatio > 1 && wideSize.height <= availableHeight) {
            this.size = wideSize;
        } else if (aspectRatio < 1 && tallSize.width <= availableWidth) {
            this.size = tallSize;
        } else {
            this.size = wideSize.height > availableHeight ? tallSize : wideSize;
        }

        this.actualSize = new Size(
            this.size.width - margin.left - margin.right,
            this.size.height - margin.top - margin.bottom
        );

        // Notify children of size and margin change
        this.plotParts.forEach((x) => {
            x.onSize(this.size, this.margin);
        });
    }

    public requestViewPort(): void {
        this.viewPort = new Rect(0, 0, this.svgWidth, this.svgHeight);
        this.viewPortBounds = this.viewPort;

        this.plotParts.forEach((x) => {
            x.onViewPortComputed(this.viewPort, this.viewPortBounds, new Pair<Unit>(Length.meter, Length.meter));
        });
    }

    public roundToNearestBin(value: number): number {
        return value;
    }

    //#endregion

    //#region Property Changed Methods

    public arrayWidthChanged(): void {
        this.updateSteps();
        this.requestViewPort();
    }

    public arrayHeightChanged(): void {
        this.updateSteps();
        this.requestViewPort();
    }

    public startCornerChanged(): void {
        // Update step order
        if (this.steps) {
            this.steps = OrderedRecordingStepGroup.sort(this.steps, this.startCorner, this.strideDirection);
        }

        this.updateSteps();
    }

    public strideDirectionChanged(): void {
        // Update step order
        if (this.steps) {
            this.steps = OrderedRecordingStepGroup.sort(this.steps, this.startCorner, this.strideDirection);
        }

        this.updateSteps();
    }

    public currentStepChanged(): void {
        this.updateSteps();
    }

    public stepsChanged(): void {
        this.updateSteps();
    }

    //#endregion

    //#region UI Methods
    public isCornerStep(step: OrderedRecordingStepGroup): Corners {
        const width = Math.max(...this.steps.map((x) => x.positionInArrays.x));
        const height = Math.max(...this.steps.map((x) => x.positionInArrays.y));

        if (step.positionInArrays.x == 0 && step.positionInArrays.y == 0) return Corners.TopLeft;
        else if (step.positionInArrays.x == width && step.positionInArrays.y == 0) return Corners.TopRight;
        else if (step.positionInArrays.x == 0 && step.positionInArrays.y == height) return Corners.BottomLeft;
        else if (step.positionInArrays.x == width && step.positionInArrays.y == height) return Corners.BottomRight;
        else return Corners.None;
    }

    public buildPath(step: OrderedRecordingStepGroup): string {
        const stepIndex = this.steps.findIndex((x) => x === step);

        const isFirstStep = stepIndex === 0;
        const isSecondStep = stepIndex === 1;
        const isLastStep = stepIndex === this.steps.length - 1;

        const dontDrawPrevArrow = isFirstStep || (isSecondStep && this.canSelectStrideDirection);
        const isPrevTop = !dontDrawPrevArrow && this.steps[stepIndex - 1].positionInArrays.y < step.positionInArrays.y;
        const isPrevRight =
            !dontDrawPrevArrow && this.steps[stepIndex - 1].positionInArrays.x > step.positionInArrays.x;
        const isPrevBottom =
            !dontDrawPrevArrow && this.steps[stepIndex - 1].positionInArrays.y > step.positionInArrays.y;
        const isPrevLeft = !dontDrawPrevArrow && this.steps[stepIndex - 1].positionInArrays.x < step.positionInArrays.x;

        const dontDrawNextArrow = isLastStep || (isFirstStep && this.canSelectStrideDirection);
        const isNextTop = !dontDrawNextArrow && this.steps[stepIndex + 1].positionInArrays.y < step.positionInArrays.y;
        const isNextRight =
            !dontDrawNextArrow && this.steps[stepIndex + 1].positionInArrays.x > step.positionInArrays.x;
        const isNextBottom =
            !dontDrawNextArrow && this.steps[stepIndex + 1].positionInArrays.y > step.positionInArrays.y;
        const isNextLeft = !dontDrawNextArrow && this.steps[stepIndex + 1].positionInArrays.x < step.positionInArrays.x;

        return (
            "M 0 0" +
            (isPrevTop || isNextTop
                ? this.lineArrow(0, 0, step.scanArea.width * this.scaleFactor, 0, isPrevTop)
                : this.lineTo(step.scanArea.width * this.scaleFactor, 0)) +
            (isPrevRight || isNextRight
                ? this.lineArrow(
                      step.scanArea.width * this.scaleFactor,
                      0,
                      step.scanArea.width * this.scaleFactor,
                      step.scanArea.height * this.scaleFactor,
                      isPrevRight
                  )
                : this.lineTo(step.scanArea.width * this.scaleFactor, step.scanArea.height * this.scaleFactor)) +
            (isPrevBottom || isNextBottom
                ? this.lineArrow(
                      step.scanArea.width * this.scaleFactor,
                      step.scanArea.height * this.scaleFactor,
                      0,
                      step.scanArea.height * this.scaleFactor,
                      isPrevBottom
                  )
                : this.lineTo(0, step.scanArea.height * this.scaleFactor)) +
            (isPrevLeft || isNextLeft
                ? this.lineArrow(0, step.scanArea.height * this.scaleFactor, 0, 0, isPrevLeft)
                : this.lineTo(0, 0)) +
            " Z"
        );
    }

    public buildPathForStrideSelection(direction: "x" | "y"): string {
        if (!this.steps) return "";

        if (direction == "x") {
            if (this.steps[0].positionInArrays.x == 0)
                return this.closedArrow(
                    this.steps[0].scanArea.width * this.scaleFactor,
                    0,
                    this.steps[0].scanArea.width * this.scaleFactor,
                    this.steps[0].scanArea.height * this.scaleFactor,
                    false
                );
            else return this.closedArrow(0, this.steps[0].scanArea.height * this.scaleFactor, 0, 0, false);
        } else {
            if (this.steps[0].positionInArrays.y == 0)
                return this.closedArrow(
                    this.steps[0].scanArea.width * this.scaleFactor,
                    this.steps[0].scanArea.height * this.scaleFactor,
                    0,
                    this.steps[0].scanArea.height * this.scaleFactor,
                    false
                );
            else return this.closedArrow(0, 0, this.steps[0].scanArea.width * this.scaleFactor, 0, false);
        }
    }

    //#region Build Path Helpers
    private lineTo(x: number, y: number): string {
        return ` L ${x} ${y}`;
    }

    private lineArrow(x1: number, y1: number, x2: number, y2: number, pointInwards: boolean): string {
        return ` L${this.arrow(x1 + (x2 - x1) / 4, y1 + (y2 - y1) / 4, x1 + (3 * (x2 - x1)) / 4, y1 + (3 * (y2 - y1)) / 4, pointInwards)} L${x2} ${y2}`;
    }

    private closedArrow(x1: number, y1: number, x2: number, y2: number, pointInwards: boolean): string {
        return ` M${this.arrow(x1 + (x2 - x1) / 4, y1 + (y2 - y1) / 4, x1 + (3 * (x2 - x1)) / 4, y1 + (3 * (y2 - y1)) / 4, pointInwards)} Z`;
    }

    private arrow(x1: number, y1: number, x2: number, y2: number, pointInwards: boolean): string {
        const arrowLength = (pointInwards ? -1 : 1) * 0.2;

        const vectorx = x1 - x2;
        const vectory = y1 - y2;

        const normx = -vectory;
        const normy = vectorx;

        const arrowx = x1 + (2 * (x2 - x1)) / 4 + arrowLength * normx;
        const arrowy = y1 + (2 * (y2 - y1)) / 4 + arrowLength * normy;

        return `${x1 + (1 * (x2 - x1)) / 4} ${y1 + (1 * (y2 - y1)) / 4} L${arrowx} ${arrowy} L${x1 + (3 * (x2 - x1)) / 4} ${y1 + (3 * (y2 - y1)) / 4}`;
    }
    //#endregion

    //#region Delegates
    public stepClicked(step: OrderedRecordingStepGroup): void {
        this.currentStep = step;

        const cornerStep = this.isCornerStep(step);
        if (this.mode == "plan" && this.isCornerStep(step)) {
            this.startCorner = cornerStep;
        }
    }

    public strideDirectionClicked(direction: "x" | "y") {
        this.strideDirection = direction;
    }

    //#endregion

    //#region Helper Methods
    private updateSteps(): void {
        this.requestMeasure();
        this.requestViewPort();
    }
    //#endregion
}
